import { useState } from 'react'
import { Link } from 'react-router-dom'
import { submitKYC } from '../services/api'

const DOC_TYPES = [
  { id: 'sa_id', label: 'South African ID' },
  { id: 'passport', label: 'Passport' },
  { id: 'drivers_licence', label: "Driver's Licence" },
]

export default function KYCPage() {
  const [step, setStep] = useState(1)
  const [docType, setDocType] = useState('')
  const [frontB64, setFrontB64] = useState('')
  const [frontPreview, setFrontPreview] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve((reader.result as string).split(',')[1])
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const b64 = await toBase64(file)
    setFrontB64(b64)
    setFrontPreview(URL.createObjectURL(file))
  }

  const handleSubmit = async () => {
    if (!frontB64) { setError('Please upload your document'); return }
    setLoading(true); setError('')
    try {
      await submitKYC({ tier_requested: 2, document_type: docType, document_front_b64: frontB64 })
      setStep(3)
    } catch (e: any) {
      setError(e.message || 'Submission failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      <div className="mx-auto max-w-lg px-4 py-12">
        <h1 className="font-heading text-2xl font-bold text-violet-900 mb-2">Verify Your Identity</h1>
        <p className="text-sm text-gray-500 mb-8">Required to unlock withdrawals</p>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-10">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex items-center ${s < 3 ? 'flex-1' : ''}`}>
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= s ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-400'}`}>{s}</div>
              {s < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > s ? 'bg-violet-600' : 'bg-gray-100'}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">Choose document type</h2>
            <div className="space-y-3 mb-8">
              {DOC_TYPES.map(dt => (
                <button key={dt.id} type="button" onClick={() => setDocType(dt.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition font-medium ${docType === dt.id ? 'border-violet-600 bg-violet-50 text-violet-900' : 'border-gray-200 text-gray-700 hover:border-violet-300'}`}>
                  {dt.label}
                </button>
              ))}
            </div>
            <button onClick={() => docType && setStep(2)} disabled={!docType}
              className="w-full min-h-[44px] bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white font-bold rounded-btn transition">
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">Upload your {DOC_TYPES.find(d => d.id === docType)?.label}</h2>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Front of document</label>
              <input type="file" accept="image/*" onChange={handleFile}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-btn file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" />
              {frontPreview && <img src={frontPreview} alt="preview" className="mt-3 h-20 w-20 object-cover rounded-lg border border-gray-200" />}
            </div>
            {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
            <button onClick={handleSubmit} disabled={loading}
              className="w-full min-h-[44px] bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white font-bold rounded-btn transition flex items-center justify-center gap-2">
              {loading ? <span className="shimmer w-24 h-4 rounded" /> : 'Submit for Verification'}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">Documents Submitted</h2>
            <p className="text-gray-500 mb-2">We'll verify your identity within 1–2 business days.</p>
            <p className="text-sm text-gray-400 mb-8">You'll be notified by email once approved.</p>
            <Link to="/account" className="inline-block px-6 py-3 bg-violet-600 text-white font-bold rounded-btn hover:bg-violet-700 transition min-h-[44px]">
              Return to Account
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
